import type { ProjectNodeInputs } from "@oblique/shared/runtime"
import { defaultProjectPythonVersion } from "@oblique/shared/runtime"
import { mutators, queries } from "@oblique/shared/zero"
import type { QueryResultType } from "@rocicorp/zero"
import { useQuery, useZero } from "@rocicorp/zero/react"
import { useEffect, useMemo } from "react"
import { v7 as uuidv7 } from "uuid"
import { imageNodeOutput } from "@/components/project/nodes/image-node"
import { usePlatform } from "@/platform/context"
import type {
	AppPlatform,
	ProjectExecutionResult,
	ProjectRuntimeNode,
	ProjectRuntimeSnapshot,
	RuntimeLogEntry,
} from "@/platform/types"
import { runtimePreparationSignature } from "./runtime-signatures"

type RuntimeSnapshot = ProjectRuntimeSnapshot & {
	signature: string
}
type RuntimeProject = QueryResultType<typeof queries.projects.executionById>

type ProjectRuntimeSyncState = {
	active: boolean
	completedGraphSignature?: string
	completedPreparationSignature?: string
	queuedSnapshot?: RuntimeSnapshot
}

const projectRuntimeSyncStates = new Map<string, ProjectRuntimeSyncState>()
const runtimeSyncDebounceMs = 850

const asStringArray = (value: unknown) => {
	if (!Array.isArray(value)) {
		return []
	}

	return value.filter((item): item is string => typeof item === "string")
}

const asRecord = (value: unknown): Record<string, unknown> => {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>
	}
	return {}
}

const asPythonDependencies = (value: unknown) => {
	if (!Array.isArray(value)) return []
	return value.flatMap((item) => {
		if (
			item &&
			typeof item === "object" &&
			typeof (item as { name?: unknown }).name === "string" &&
			typeof (item as { version?: unknown }).version === "string"
		) {
			return [item as { name: string; version: string }]
		}
		return []
	})
}

const asJSDependencies = asPythonDependencies

const runtimeLog = (
	platform: AppPlatform,
	level: RuntimeLogEntry["level"],
	message: string,
	fields?: RuntimeLogEntry["fields"],
) => {
	platform.logRuntimeEvent?.({
		fields,
		level,
		message,
		timestamp: Date.now(),
	})
}

const runtimeSnapshotFromProject = (project: RuntimeProject) => {
	if (!project) {
		return null
	}

	const nodes = project.project_nodes
		.map((node) => {
			const inputs = asRecord(node.inputs) as ProjectNodeInputs
			const staticImageOutput = imageNodeOutput(asRecord(node.metadata), inputs)
			return {
				code: node.code,
				id: node.id,
				inputs,
				label: node.label,
				outputs: asStringArray(node.outputs),
				type: node.type === "react" ? "react" : "python",
				latestSerializedOutput:
					node.geometries[0]?.serialized_data ??
					(staticImageOutput === undefined
						? undefined
						: JSON.stringify(staticImageOutput)),
			} satisfies ProjectRuntimeNode
		})
		.sort((left, right) => left.id.localeCompare(right.id))

	const snapshot = {
		bunLock: project.bun_lock ?? null,
		bunLockHash: project.bun_lock_hash ?? null,
		dependencies: [],
		edges: project.project_edges.map((edge) => ({
			id: edge.id,
			sourceHandle: edge.source_handle || undefined,
			sourceNodeID: edge.source_node_id,
			targetHandle: edge.target_handle || undefined,
			targetNodeID: edge.target_node_id,
		})),
		jsDependencies: asJSDependencies(project.js_dependencies),
		jsRuntimeVersion:
			typeof project.js_runtime_version === "number"
				? project.js_runtime_version
				: 1,
		name: project.name,
		nodes: nodes.map((node) => ({
			code: node.code,
			id: node.id,
			inputs: node.inputs,
			label: node.label,
			latestSerializedOutput: node.latestSerializedOutput,
			outputs: node.outputs,
			type: node.type,
		})),
		projectID: project.id,
		pythonDependencies: asPythonDependencies(project.python_dependencies),
		pythonVersion: project.python_version ?? defaultProjectPythonVersion,
		uvLock: project.uv_lock ?? null,
	}

	return {
		...snapshot,
		signature: JSON.stringify({
			...snapshot,
			nodes: snapshot.nodes.map(
				({ latestSerializedOutput: _latestSerializedOutput, ...node }) => node,
			),
		}),
	} satisfies RuntimeSnapshot
}

const setupPythonRuntime = async (
	platform: AppPlatform,
	snapshot: RuntimeSnapshot,
) => {
	runtimeLog(platform, "info", "runtime snapshot ready", {
		dependencies: snapshot.dependencies.length,
		nodes: snapshot.nodes.length,
		projectID: snapshot.projectID,
	})
	runtimeLog(platform, "info", "python environment setup starting", {
		projectID: snapshot.projectID,
	})

	const result = await platform.prepareProjectExecution?.(snapshot)

	runtimeLog(platform, "info", "python environment setup ready", {
		directoryPath: result?.workspace.directoryPath,
		projectID: snapshot.projectID,
	})
	return result
}

const schedulePythonRuntimeSetup = (
	platform: AppPlatform,
	snapshot: RuntimeSnapshot,
	onPrepared: (result: ProjectExecutionResult) => Promise<void>,
) => {
	const state =
		projectRuntimeSyncStates.get(snapshot.projectID) ??
		({ active: false } satisfies ProjectRuntimeSyncState)

	projectRuntimeSyncStates.set(snapshot.projectID, state)

	if (state.completedGraphSignature === snapshot.signature) {
		return
	}

	if (state.active) {
		state.queuedSnapshot = snapshot
		runtimeLog(platform, "info", "python environment setup queued", {
			projectID: snapshot.projectID,
		})
		return
	}

	state.active = true

	const run = async (initialSnapshot: RuntimeSnapshot) => {
		let currentSnapshot: RuntimeSnapshot | undefined = initialSnapshot

		while (currentSnapshot) {
			state.queuedSnapshot = undefined
			const preparationSignature = runtimePreparationSignature(currentSnapshot)

			try {
				if (state.completedPreparationSignature !== preparationSignature) {
					const result = await setupPythonRuntime(platform, currentSnapshot)
					if (
						result &&
						(result.uvLock !== currentSnapshot.uvLock ||
							result.bunLock !== currentSnapshot.bunLock ||
							result.bunLockHash !== currentSnapshot.bunLockHash)
					) {
						try {
							await onPrepared(result)
						} catch (error) {
							await platform.rollbackProjectExecution?.(currentSnapshot)
							throw error
						}
					}
					state.completedPreparationSignature = preparationSignature
				}
				await platform.updateProjectGraph?.(currentSnapshot)
				state.completedGraphSignature = currentSnapshot.signature
			} catch (error) {
				runtimeLog(platform, "error", "Project preparation failed", {
					error: error instanceof Error ? error.message : String(error),
					projectID: currentSnapshot.projectID,
				})
			}

			const queuedSnapshot = state.queuedSnapshot as RuntimeSnapshot | undefined
			currentSnapshot =
				queuedSnapshot?.signature &&
				queuedSnapshot.signature !== state.completedGraphSignature
					? queuedSnapshot
					: undefined
		}

		state.active = false
	}

	void run(snapshot)
}

export const useOriginProjectRuntime = ({
	enabled,
	projectID,
}: {
	enabled: boolean
	projectID?: string
}) => {
	const platform = usePlatform()
	const z = useZero()
	const [runtimeProject, runtimeProjectStatus] = useQuery(
		queries.projects.executionById(projectID ?? ""),
	)
	const runtimeSnapshot = useMemo(
		() =>
			runtimeProjectStatus.type === "complete"
				? runtimeSnapshotFromProject(runtimeProject)
				: null,
		[runtimeProject, runtimeProjectStatus.type],
	)

	useEffect(() => {
		if (
			!enabled ||
			!projectID ||
			!runtimeSnapshot ||
			!platform.prepareProjectExecution
		) {
			return
		}

		runtimeLog(platform, "info", "runtime snapshot changed", {
			projectID,
		})
		const removeMutationListener = platform.onProjectMutation?.((request) => {
			if (request.projectID !== projectID) return
			const run = async () => {
				if (request.type === "create_edge") {
					return await z.mutate(
						mutators.edges.add({
							id: String(request.payload.edge_id ?? ""),
							project_id: projectID,
							source_handle: String(request.payload.source_handle ?? ""),
							source_node_id: String(request.payload.source_node_id ?? ""),
							target_handle: String(request.payload.target_handle ?? ""),
							target_node_id: String(request.payload.target_node_id ?? ""),
						}),
					).server
				}
				if (request.type === "create_node") {
					const position = asRecord(request.payload.position)
					return await z.mutate(
						mutators.nodes.add({
							code: String(request.payload.code ?? ""),
							id: String(request.payload.node_id ?? ""),
							inputs: asRecord(request.payload.inputs) as ProjectNodeInputs,
							label: String(request.payload.label ?? ""),
							metadata: asRecord(request.payload.metadata),
							outputs: asStringArray(request.payload.outputs),
							position:
								typeof position.x === "number" && typeof position.y === "number"
									? { x: position.x, y: position.y }
									: undefined,
							project_id: projectID,
							type: request.payload.type === "react" ? "react" : "python",
						}),
					).server
				}
				if (request.type === "remove_node") {
					return await z.mutate(
						mutators.nodes.delete({
							node_id: String(request.payload.node_id ?? ""),
							project_id: projectID,
						}),
					).server
				}
				if (request.type === "remove_edge") {
					return await z.mutate(
						mutators.edges.delete({
							edge_id: String(request.payload.edge_id ?? ""),
							project_id: projectID,
						}),
					).server
				}
				if (request.type === "update_node_definition") {
					return await z.mutate(
						mutators.nodes.updateDefinition({
							code: String(request.payload.code ?? ""),
							inputs: asRecord(request.payload.inputs) as ProjectNodeInputs,
							label: String(request.payload.label ?? ""),
							metadata:
								request.payload.metadata === undefined
									? undefined
									: asRecord(request.payload.metadata),
							node_id: String(request.payload.node_id ?? ""),
							outputs: asStringArray(request.payload.outputs),
							project_id: projectID,
						}),
					).server
				}
				if (request.type === "update_dependencies") {
					return await z.mutate(
						mutators.projects.updateDependencies({
							project_id: projectID,
							python_dependencies: asPythonDependencies(
								request.payload.python_dependencies,
							),
							uv_lock:
								typeof request.payload.uv_lock === "string"
									? request.payload.uv_lock
									: null,
						}),
					).server
				}
				if (request.type === "update_js_dependencies") {
					return await z.mutate(
						mutators.projects.updateJSDependencies({
							project_id: projectID,
							js_dependencies: asJSDependencies(
								request.payload.js_dependencies,
							),
							bun_lock:
								typeof request.payload.bun_lock === "string"
									? request.payload.bun_lock
									: null,
							bun_lock_hash:
								typeof request.payload.bun_lock_hash === "string"
									? request.payload.bun_lock_hash
									: null,
							js_runtime_version:
								typeof request.payload.js_runtime_version === "number"
									? request.payload.js_runtime_version
									: 1,
						}),
					).server
				}
				throw new Error(`Unsupported project mutation: ${request.type}`)
			}
			void run()
				.then((result) => {
					if (result.type === "error") throw new Error(result.error.message)
					platform.completeProjectMutation?.(request.id, { success: true })
				})
				.catch((error) =>
					platform.completeProjectMutation?.(request.id, {
						error: error instanceof Error ? error.message : String(error),
						success: false,
					}),
				)
		})
		const removeOutputListener = platform.onProjectNodeOutput?.((event) => {
			if (event.projectID !== projectID) return
			const serializedData = JSON.stringify(event.output)
			if (typeof serializedData !== "string") {
				runtimeLog(platform, "error", "Node output is not serializable", {
					nodeID: event.nodeID,
					projectID,
				})
				return
			}
			const createdAt = Date.now()
			void z
				.mutate(
					mutators.runtimeOutputs.record({
						created_at: createdAt,
						id: uuidv7(),
						node_id: event.nodeID,
						project_id: projectID,
						serialized_data: serializedData,
					}),
				)
				.server.then((result) => {
					if (result.type === "error") {
						runtimeLog(platform, "error", "Node output persistence failed", {
							error: result.error.message,
							nodeID: event.nodeID,
							projectID,
						})
					}
				})
		})

		const syncState =
			projectRuntimeSyncStates.get(projectID) ??
			({ active: false } satisfies ProjectRuntimeSyncState)
		projectRuntimeSyncStates.set(projectID, syncState)
		const preparationChanged =
			syncState.completedPreparationSignature !==
			runtimePreparationSignature(runtimeSnapshot)
		const syncSnapshot = () =>
			schedulePythonRuntimeSetup(
				platform,
				runtimeSnapshot,
				async (prepared) => {
					const pythonResult = await z.mutate(
						mutators.projects.updateDependencies({
							project_id: projectID,
							python_dependencies: runtimeSnapshot.pythonDependencies,
							uv_lock: prepared.uvLock,
						}),
					).server
					if (pythonResult.type === "error")
						throw new Error(pythonResult.error.message)
					const jsResult = await z.mutate(
						mutators.projects.updateJSDependencies({
							project_id: projectID,
							js_dependencies: runtimeSnapshot.jsDependencies,
							bun_lock: prepared.bunLock,
							bun_lock_hash: prepared.bunLockHash,
							js_runtime_version: runtimeSnapshot.jsRuntimeVersion,
						}),
					).server
					if (jsResult.type === "error") throw new Error(jsResult.error.message)
				},
			)
		let timeout: ReturnType<typeof setTimeout> | undefined
		if (preparationChanged) {
			timeout = setTimeout(syncSnapshot, runtimeSyncDebounceMs)
		} else {
			syncSnapshot()
		}

		return () => {
			if (timeout !== undefined) clearTimeout(timeout)
			removeMutationListener?.()
			removeOutputListener?.()
		}
	}, [enabled, platform, projectID, runtimeSnapshot, z])
}
